import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { closePopup, getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import Modal from 'components/base/Modal';
import { selectPlanDetails } from 'data/user/selectors';
import checkCircleTeal from 'assets/images/check-circle-Teal.svg';

import css from './style.scss';

// The Investor Outreach upsell overlay (ATAI-260 / S7).
//
// Drawn over the locked Investor Outreach feature route for a primary account
// holder who has not subscribed yet — the "navigate first, then overlay"
// discovery surface from flow 2. Built per Figma (frame 925:54082): headline,
// product-visual panel, four value bullets, the Connect requirement line, and
// an "Unlock Now" button. Registered through the PopupHolder pattern, so it is
// opened with `InvestorOutreachUpsell.open({ ... })` and overlays the current
// screen rather than navigating.
//
// Wiring that belongs to other stories is exposed as callbacks rather than
// hard-coded here:
//   - onPurchase              → the S8 purchase page
//   - onStartTrialConversion  → the trial holder's Unlock confirmation, which then runs the
//                               shared Activate Subscription modal
// The trial branch is chosen from planDetails.trialAvailable, matching the
// existing Lead Automator upgrade overlay.
//
// Unlock Now does NOT dismiss this overlay. Whatever the callback opens stacks on top of it at
// a higher popup priority, and the owner (../index.js) decides when the overlay itself goes --
// which is what lets a cancelled unlock land the customer back here rather than on a blank
// locked page.

// Supademo interactive tour, embedded in the product-visual slot.
const SUPADEMO_EMBED_URL = 'https://app.supademo.com/embed/cmv6y9qxw05t9qmuqv56e5021';

const VALUE_BULLETS = [
  'Discover the most active buyers in any market',
  'Analyze investor portfolios and acquisition behavior',
  'Build targeted buyer lists instantly',
  'Launch outreach campaigns using built-in marketing tools',
];

// Lock-open glyph for the primary button (Figma: Buttons/Text Button icon).
// Inlined so the button can tint it via currentColor rather than shipping a
// one-off asset.
const LockOpenIcon = () => (
  <svg
    className={css.clsUnlockIcon}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

const InvestorOutreachUpsell = (props) => {
  const { productVisual, onPurchase, onStartTrialConversion, onClose } = props;

  const planDetails = useSelector(selectPlanDetails)?.toJS();
  const isTrial = Boolean(planDetails?.trialAvailable);

  const handleClose = () => {
    props.closePopup && props.closePopup();
    onClose && onClose();
  };

  const handleUnlockNow = () => {
    // Hand off to the branch this customer belongs in; see the note above on why this does not
    // close itself first.
    if (isTrial) {
      onStartTrialConversion && onStartTrialConversion();
    } else {
      onPurchase && onPurchase();
    }
  };

  return (
    <Modal
      isOpen
      uniqId="InvestorOutreachUpsellOverlay"
      isCloseButton={false}
      // The upsell gates a locked feature: it must not be dismissable. No close button, no
      // backdrop-click close, no Escape — the only ways out are Unlock Now or navigating away.
      closeOnOutsideClick={false}
      ignoreEsc
      width="auto"
      padding="0px"
      zIndex={998}
      modalClassName={css.clsUpsellModal}
      className={classNames(css.clsUpsellOverlay)}
      onClose={handleClose}
    >
      <div className={css.clsWrapper}>
        <div className={css.clsMain}>
          {/* Product-visual slot — a caller-supplied visual wins; otherwise it embeds the
              Supademo tour. */}
          <div className={css.clsVisual}>
            {productVisual || (
              <iframe
                className={css.clsTourFrame}
                src={SUPADEMO_EMBED_URL}
                title="Investor Outreach product tour"
                loading="lazy"
                allow="clipboard-write; fullscreen"
                allowFullScreen
              />
            )}
          </div>

          <div className={css.clsContent}>
            {/* Trial/Winback banner (PRD). Shown only to trial users. Figma frame
                925:54079 does not draw it, so the copy is taken verbatim from the PRD
                and the placement/styling here is provisional — confirm with design
                (flagged on ATAI-260). */}
            {isTrial && (
              <div className={css.clsTrialBanner}>
                You are currently on a Free Trial. Subscribing to Investor Outreach will start your
                paid PropStream subscription immediately.
              </div>
            )}

            <h2 className={css.clsTitle}>Unlock Investor Outreach</h2>

            {/* Figma 925:54324 — description and bullets are grouped at a 15px gap. */}
            <div className={css.clsDetails}>
              <p className={css.clsDescription}>
                Upgrade to Investor Outreach to search any property address and instantly discover
                nearby investors with a proven history of purchasing similar properties.
              </p>

              {/* Figma 925:54327 — the requirement line sits with the bullets at an 8px gap. */}
              <div className={css.clsBulletsGroup}>
                <ul className={css.clsBullets}>
                  {VALUE_BULLETS.map((text) => (
                    <li key={text} className={css.clsBullet}>
                      <img
                        className={css.clsBulletIcon}
                        src={checkCircleTeal}
                        alt=""
                        aria-hidden="true"
                      />
                      <span className={css.clsBulletText}>{text}</span>
                    </li>
                  ))}
                </ul>

                <p className={css.clsRequirement}>
                  *Requires an active PropStream Connect subscription
                </p>
              </div>
            </div>

            {/* TODO(S8 · ATAI-261): show the dynamic price for the customer's own plan tier and
                interval, from the same S3 preview the purchase page uses. Figma 925:54079 draws no
                price slot, so its placement needs design confirmation (flagged on ATAI-260). */}

            <button type="button" className={css.clsUnlockBtn} onClick={handleUnlockNow}>
              <LockOpenIcon />
              <span className={css.clsUnlockLabel}>Unlock Now</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const registrationId = getPopupRegistration(InvestorOutreachUpsell);
InvestorOutreachUpsell.open = (props = {}) =>
  openPopup(registrationId, { ...props, priority: Priority.MEDIUM });
InvestorOutreachUpsell.close = () => closePopup({ popup: registrationId });

export default InvestorOutreachUpsell;
